import React, { useMemo } from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import {
    Column,
    FilterFieldCollection,
    FilterOperation,
    FilterType,
    LoadTableDataInterface,
} from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";
import { OptionType } from "commons/ui/input/Select";

import { makeUrl } from "commons/utils/http";

import useMaterialApi from "hooks/api/private/useMaterialApi";
import useMaterialCategoryApi from "hooks/api/private/useMaterialCategoryApi";
import useMaterialSubcategoryApi from "hooks/api/private/useMaterialSubcategoryApi";
import useSnackbar from "hooks/useSnackbar";

import { MaterialListItem } from "types/api/material";
import { MaterialCategoryListItem } from "types/api/materialCategory";

import { emptyListRequest } from "utils/utils";

export type MaterialsListHook = {
    navigateToNewMaterial: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    categoryOptions: OptionType[];
    subcategoryOptions: OptionType[];
    typeOptions: OptionType[];
    dataTableHook: DataTableHook<MaterialListItem>;
    alertConfirmHook: AlertConfirmHookType;
};

function useMaterialsList(): MaterialsListHook {
    const navigate = useNavigate();

    const { getAllMaterial, deleteMaterial } = useMaterialApi();
    const { getAllMaterialCategory } = useMaterialCategoryApi();
    const { getAllMaterialSubcategory } = useMaterialSubcategoryApi();

    const onEditClick = React.useCallback(
        (row: MaterialListItem) => {
            navigate(makeUrl(":materialId/modifica", { materialId: row.id }));
        },
        [navigate]
    );

    const navigateToNewMaterial = React.useCallback(() => {
        navigate("nuovo");
    }, [navigate]);

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    art_rr: {
                        key: "material_languages.art_rr",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    code_rr: {
                        key: "material_languages.code_rr",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    category_name: {
                        key: "material_category_languages.name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    subcategory_name: {
                        key: "material_subcategory_languages.name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        []
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "material-list-search",
        memoizedFieldsForSearch
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.art_rr = value;
                newForm.search.code_rr = value;
                newForm.search.category_name = value;
                newForm.search.subcategory_name = value;

                return newForm;
            }),
        [setFiltersForm]
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            type: {
                key: "material_category.type",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            material_category_id: {
                key: "material.material_category_id",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            material_subcategory_id: {
                key: "material.material_subcategory_id",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        []
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "material-list-static",
        memoizedFieldsForStaticFilters
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters, form: staticFilterForm, setValue: setStaticFilterValue } =
        staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters]
    );

    const [allCategoryOptions, setAllCategoryOptions] = React.useState<MaterialCategoryListItem[]>([]);

    const [categoryOptions, setCategoryOptions] = React.useState<OptionType[]>([]);
    const [subcategoryOptions, setSubcategoryOptions] = React.useState<OptionType[]>([]);

    const typeOptions = useMemo(() => {
        return [
            { value: "", label: "Tutte" },
            { value: "indoor", label: "Indoor" },
            { value: "outdoor", label: "Outdoor" },
        ];
    }, []);

    React.useEffect(() => {
        const [apiCall, abortController] = getAllMaterialCategory(emptyListRequest);

        apiCall.then((response) => {
            setAllCategoryOptions(response.content);
        });

        return () => abortController.abort();
    }, [getAllMaterialCategory]);

    React.useEffect(() => {
        const filteredCategories = staticFilterForm.type
            ? allCategoryOptions.filter((c) => c.type === staticFilterForm.type)
            : allCategoryOptions;

        const sortedCategories = [...filteredCategories].sort((a, b) => a.name.localeCompare(b.name));

        setCategoryOptions([
            { value: "", label: "Tutte" },
            ...sortedCategories.map((c) => ({ value: c.id, label: `${c.name} - ${c.type}` })),
        ]);
    }, [allCategoryOptions, staticFilterForm.type]);

    React.useEffect(() => {
        if (
            allCategoryOptions.length > 0 &&
            staticFilterForm.type &&
            staticFilterForm.material_category_id &&
            !allCategoryOptions.some(
                (c) => c.id === staticFilterForm.material_category_id && c.type === staticFilterForm.type
            )
        ) {
            setStaticFilterValue("material_category_id", "");
        }
    }, [
        allCategoryOptions,
        staticFilterForm.type,
        staticFilterForm.material_category_id,
        setStaticFilterValue,
    ]);

    React.useEffect(() => {
        const [apiCall, abortController] = getAllMaterialSubcategory(emptyListRequest);

        apiCall.then((response) => {
            const sortedSubcategories = [...response.content].sort((a, b) => a.name.localeCompare(b.name));

            setSubcategoryOptions([
                { value: "", label: "Tutte" },
                ...sortedSubcategories.map((c) => ({ value: c.id, label: c.name })),
            ]);
        });

        return () => abortController.abort();
    }, [getAllMaterialSubcategory]);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Materiale",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteMaterialButton = React.useCallback(
        (row: MaterialListItem, loadTableData: LoadTableDataInterface) => {
            openConfirmAlert("Sei sicuro di voler eliminare il materiale?", () => {
                const [apiCall] = deleteMaterial({ id: row.id });

                apiCall
                    .then(() => {
                        enqueueSuccessSnackbar("Materiale eliminato con successo");
                        loadTableData();
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del materiale"));
            });
        },
        [enqueueSuccessSnackbar, enqueueErrorSnackbar, openConfirmAlert, deleteMaterial]
    );

    const columns: Array<Column<MaterialListItem>> = React.useMemo(
        () => [
            {
                id: "composite_graphical_order",
                header: "Ordine",
                width: "85px",
                render: (row: MaterialListItem) => row.graphical_order,
                orderable: true,
            },
            {
                id: "type",
                header: "Tipologia",
                width: "100px",
                render: (row: MaterialListItem) => row.type,
                orderable: true,
            },
            {
                id: "art_rr_code_rr",
                header: "Nome",
                render: (row: MaterialListItem) => `${row.art_rr ?? ""} - ${row.code_rr ?? ""}`,
                orderable: true,
            },
            {
                id: "category_name",
                header: "Categoria",
                width: "120px",
                render: (row: MaterialListItem) => row.category_name,
                orderable: true,
            },
            {
                id: "subcategory_name",
                header: "Sottocategoria",
                width: "170px",
                render: (row: MaterialListItem) => row.subcategory_name,
                orderable: true,
            },
            {
                id: "enabled",
                header: "Abilitato",
                width: "85px",
                render: (row: MaterialListItem) => (row.enabled ? "Si" : "No"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: MaterialListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={onEditClick}
                        onDelete={deleteMaterialButton}
                    />
                ),
            },
        ],
        [onEditClick, deleteMaterialButton]
    );

    const dataTableHook = useDataTableWithMemory<MaterialListItem>({
        apiCall: getAllMaterial,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "material-list",
    });

    return React.useMemo(
        (): MaterialsListHook => ({
            onSearchFilterChange,
            navigateToNewMaterial,
            filterSearchHook,
            staticFilterHook,
            categoryOptions,
            subcategoryOptions,
            typeOptions,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            onSearchFilterChange,
            navigateToNewMaterial,
            filterSearchHook,
            staticFilterHook,
            categoryOptions,
            subcategoryOptions,
            typeOptions,
            alertConfirmHook,
            dataTableHook,
        ]
    );
}

export default useMaterialsList;
